import type {
  StoryElements,
  StoryElementsCreationInput,
  StoryElementsStats
} from '@/types';
import {
  calculateStats,
  createEmptyStoryElements,
  STORY_ELEMENTS_STORAGE_KEY
} from '@/utils/storyElementsHelpers';
import { BaseService } from './baseService';

interface StoragePayload {
  version: string;
  lastUpdated: string;
  data: StoryElements;
}

interface StorageAdapter {
  read: () => StoryElements | null;
  write: (storyElements: StoryElements) => void;
  clear: () => void;
}

const STORAGE_VERSION = '1.0.0';

class StoryElementsService extends BaseService<StoryElements> {
  constructor() {
    super({ storagePrefix: STORY_ELEMENTS_STORAGE_KEY });
  }

  create(input: StoryElementsCreationInput): StoryElements {
    return createEmptyStoryElements(input.manuscriptId);
  }

  ensureStats(storyElements: StoryElements): StoryElementsStats {
    return calculateStats(storyElements);
  }

  storage(manuscriptId: string): StorageAdapter {
    const key = this.storageKey(manuscriptId);

    const read = (): StoryElements | null => {
      if (typeof window === 'undefined') {
        return null;
      }

      const raw = window.localStorage.getItem(key);
      if (!raw) {
        return null;
      }

      try {
        const payload = JSON.parse(raw) as StoragePayload;
        if (payload.version !== STORAGE_VERSION || !payload.data) {
          window.localStorage.removeItem(key);
          return null;
        }
        return payload.data;
      } catch (error) {
        window.localStorage.removeItem(key);
        return null;
      }
    };

    const write = (storyElements: StoryElements): void => {
      if (typeof window === 'undefined') {
        return;
      }
      const payload: StoragePayload = {
        version: STORAGE_VERSION,
        lastUpdated: this.now(),
        data: storyElements
      };
      window.localStorage.setItem(key, JSON.stringify(payload));
    };

    const clear = (): void => {
      if (typeof window === 'undefined') {
        return;
      }
      window.localStorage.removeItem(key);
    };

    return { read, write, clear };
  }
}

export const storyElementsService = new StoryElementsService();
